import { Link, useParams, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import RankingRow from '@/components/feature/RankingRow';
import useSeo from '@/hooks/useSeo';
import { genres, getRankedComics, rankingMeta, type RankingType } from '@/mocks/comics';

const seoTitles: Record<RankingType, string> = {
  popularity: '韩漫人气榜 - 当下最热门的韩国漫画排行',
  rating: '韩漫评分榜 - 高分韩国漫画口碑排行',
  'must-read': '韩漫必看榜 - 入坑必读的韩国漫画清单',
};

const typeIcons: Record<RankingType, string> = {
  popularity: 'ri-fire-fill',
  rating: 'ri-star-fill',
  'must-read': 'ri-medal-fill',
};

export default function Ranking() {
  const { type = 'popularity' as RankingType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentType: RankingType = (['popularity', 'rating', 'must-read'] as RankingType[]).includes(type as RankingType)
    ? (type as RankingType)
    : 'popularity';

  const meta = rankingMeta[currentType];
  const query = searchParams.get('q') ?? '';
  const genre = searchParams.get('genre') ?? '';

  let list = getRankedComics(currentType);
  if (genre) list = list.filter((c) => c.genre === genre);
  if (query) {
    const kw = query.toLowerCase();
    list = list.filter((c) =>
      [c.title, c.genre, c.author, ...c.tags].join(' ').toLowerCase().includes(kw),
    );
  }

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const typeIndex: Record<RankingType, number> = { popularity: 0, rating: 1, 'must-read': 2 };
  const breadcrumb = [
    { label: '首页', to: '/' },
    { label: meta.label, to: `/ranking/${currentType}` },
  ];

  useSeo({
    title: seoTitles[currentType],
    description: meta.description,
    keywords: `韩国漫画排行榜,${meta.label},好看的韩国漫画,高分韩漫,韩漫必看`,
    canonicalPath: `/ranking/${currentType}`,
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: seoTitles[currentType],
        description: meta.description,
        inLanguage: 'zh-CN',
        url: `${window.location.origin}${(window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? ''}/ranking/${currentType}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.label,
          item: `${window.location.origin}${(window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? ''}${b.to}`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${meta.label}韩国漫画`,
        itemListElement: list.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.title,
          url: `${window.location.origin}${(window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? ''}/comic/${c.id}`,
        })),
      },
    ],
  });

  return (
    <Layout>
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{meta.label}</span>
          </nav>

          <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <span className={`flex items-center gap-2 text-sm font-semibold ${currentType === 'rating' ? 'text-accent-600' : 'text-primary-600'}`}>
                <i className={`${typeIcons[currentType]} text-base`} />
                韩国漫画榜单
              </span>
              <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">{meta.label}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{meta.description}</p>
            </div>
            <p className="shrink-0 text-sm text-foreground-500">
              共 <span className="font-bold text-primary-700">{list.length}</span> 部作品
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(rankingMeta) as RankingType[]).map((t) => (
            <Link
              key={t}
              to={`/ranking/${t}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                t === currentType ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {rankingMeta[t].label}
            </Link>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-5 lg:flex-row">
          <aside className="shrink-0 lg:w-52">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
              <i className="ri-filter-3-line" /> 按分类筛选
            </h3>
            <div className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-1.5">
              <button
                onClick={() => updateFilter('genre', '')}
                className={`rounded-full px-3.5 py-1.5 text-left text-sm transition-colors ${
                  !genre ? 'bg-secondary-100 font-medium text-secondary-900' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                全部类型
              </button>
              {genres.map((g) => (
                <button
                  key={g}
                  onClick={() => updateFilter('genre', g)}
                  className={`rounded-full px-3.5 py-1.5 text-left text-sm transition-colors ${
                    genre === g ? 'bg-secondary-100 font-medium text-secondary-900' : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </aside>

          <div className="flex-1">
            {(genre || query) && (
              <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
                <span>当前筛选：</span>
                {genre && (
                  <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                    {genre}
                    <button onClick={() => updateFilter('genre', '')} aria-label="清除分类">
                      <i className="ri-close-line" />
                    </button>
                  </span>
                )}
                {query && (
                  <span className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-primary-700">
                    “{query}”
                    <button onClick={() => updateFilter('q', '')} aria-label="清除搜索">
                      <i className="ri-close-line" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {list.length > 0 ? (
              <div className="space-y-1 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4">
                {list.map((comic, i) => (
                  <RankingRow key={comic.id} comic={comic} rank={i + 1} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <i className="ri-search-line text-4xl text-foreground-300" />
                <p className="mt-3 font-heading text-lg font-bold text-foreground-800">没有找到匹配的作品</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词或分类试试吧</p>
                <button
                  onClick={() => setSearchParams({}, { replace: true })}
                  className="mt-5 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}